import { useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard,
  Scissors,
  LogOut,
  Menu,
  X,
  Sparkles,
  User,
  Camera,
  Trash2,
  Loader2,
  History,
  Users,
  UsersRound,
  Settings,
  PlusCircle,
  UserCircle,
  Hammer,
  CalendarDays,
  LifeBuoy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import NewBadge from '../components/NewBadge';
import SupportWidget from './SupportWidget';
import { toast } from 'sonner';
import useWin98Scope from '../hooks/useWin98Scope';

// Routes that get the Windows-98-modernized design pass. LandingPage, Admin,
// and PublicCreatorPage (its own per-creator theme system) keep the existing
// look; every other route is either listed here or toggles the same
// .win98-scope class itself via useWin98Scope() since it renders outside
// Layout entirely (auth pages, Explore, SharedCosplayPage, anonymous
// CommissionPage — see index.css for the .win98-scope rules).
const WIN98_SCOPE_PREFIXES = ['/dashboard', '/cosplays', '/history', '/groups', '/cons', '/settings', '/profile', '/create', '/social', '/commission'];

const Layout = ({ children }) => {
  const { user, logout, uploadProfilePicture, deleteProfilePicture } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const fileInputRef = useRef(null);

  const isWin98Scope = WIN98_SCOPE_PREFIXES.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
  );

  useWin98Scope(isWin98Scope);

  // Build nav items dynamically based on user's creator_role
  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/cosplays', icon: Scissors, label: 'Cosplays' },
    { path: '/social', icon: Users, label: 'Social' },
    { path: '/groups', icon: UsersRound, label: 'Groups', badge: '2026-06-20' },
    { path: '/commission', icon: Hammer, label: 'Commission', badge: '2026-06-20' },
    { path: '/cons', icon: CalendarDays, label: 'Cons', badge: '2026-09-11' },
    { path: '/history', icon: History, label: 'History' },
    { path: '/profile', icon: UserCircle, label: 'Profile' },
    { path: '/settings', icon: Settings, label: 'Settings' },
    // Add Create tab at the bottom if user has creator_role
    ...(user?.creator_role ? [{ path: '/create', icon: PlusCircle, label: 'Create' }] : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      await uploadProfilePicture(file);
      toast.success('Profile picture updated!');
      setShowProfileModal(false);
    } catch (error) {
      toast.error('Failed to upload profile picture');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePicture = async () => {
    setUploading(true);
    try {
      await deleteProfilePicture();
      toast.success('Profile picture removed');
      setShowProfileModal(false);
    } catch (error) {
      toast.error('Failed to remove profile picture');
    } finally {
      setUploading(false);
    }
  };

  const ProfileAvatar = ({ size = 'md', onClick }) => {
    const sizeClasses = {
      sm: 'w-8 h-8',
      md: 'w-10 h-10',
      lg: 'w-20 h-20'
    };

    const iconSizes = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-10 h-10'
    };

    return (
      <button 
        onClick={onClick}
        className={`${sizeClasses[size]} rounded-full overflow-hidden flex-shrink-0 relative group`}
      >
        {user?.profile_picture ? (
          <img 
            src={`${process.env.REACT_APP_BACKEND_URL || ''}${user.profile_picture}`}
            alt={user.display_name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className={`${sizeClasses[size]} bg-gradient-to-br from-cyan-500 to-fuchsia-500 flex items-center justify-center`}>
            <User className={`${iconSizes[size]} text-white`} />
          </div>
        )}
        {onClick && (
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Camera className="w-4 h-4 text-white" />
          </div>
        )}
      </button>
    );
  };

  const NavContent = () => (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-white/10">
        <Link to="/dashboard" className="flex items-center gap-3" data-testid="nav-logo">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-fuchsia-500 flex items-center justify-center shadow-glow-cyan">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-heading font-bold text-white">KyberCosplay</span>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path ||
                          (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              data-active={isActive}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                isActive
                  ? 'bg-cyan-950/50 text-cyan-400 border border-cyan-500/20 shadow-[0_0_10px_rgba(6,182,212,0.1)]'
                  : 'text-slate-400 hover:text-cyan-400 hover:bg-cyan-950/30'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
              {item.badge && <NewBadge since={item.badge} className="ml-auto" />}
            </Link>
          );
        })}
        <button
          onClick={() => { setSupportOpen(true); setSidebarOpen(false); }}
          data-testid="nav-support"
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-slate-400 hover:text-cyan-400 hover:bg-cyan-950/30"
        >
          <LifeBuoy className="w-5 h-5" />
          <span className="font-medium">Support</span>
        </button>
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-white/10">
        <div 
          className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-900/50 mb-3 cursor-pointer hover:bg-slate-800/50 transition-colors"
          onClick={() => setShowProfileModal(true)}
          data-testid="user-profile-btn"
        >
          <ProfileAvatar size="md" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.display_name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
          <Camera className="w-4 h-4 text-slate-500" />
        </div>
        <Button
          onClick={handleLogout}
          variant="ghost"
          data-testid="logout-btn"
          className="w-full justify-start gap-3 text-slate-400 hover:text-red-400 hover:bg-red-950/20 rounded-xl"
        >
          <LogOut className="w-5 h-5" />
          Sign Out
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col glass border-r border-white/10 fixed inset-y-0 left-0 z-30">
        <NavContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside className={`lg:hidden fixed inset-y-0 left-0 z-50 w-64 flex flex-col glass border-r border-white/10 transform transition-transform duration-300 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <NavContent />
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64 min-w-0 overflow-x-hidden">
        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 z-20 bg-slate-900/95 backdrop-blur-xl border-b border-white/10 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-xl text-cyan-400 hover:text-white hover:bg-cyan-950/30 transition-colors"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-fuchsia-500 flex items-center justify-center shadow-glow-cyan">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-bold text-white">KyberCosplay</span>
          </div>
          <div className="w-10" /> {/* Spacer for centering */}
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8 overflow-x-hidden">
          {children}
        </main>
      </div>

      {/* Profile Picture Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="bg-slate-900/95 border-white/10 backdrop-blur-xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-white text-center">Profile Picture</DialogTitle>
          </DialogHeader>
          
          <div className="flex flex-col items-center gap-6 py-4">
            {/* Large Avatar Preview */}
            <div className="relative">
              <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white/10">
                {user?.profile_picture ? (
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL || ''}${user.profile_picture}`}
                    alt={user.display_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-cyan-500 to-fuchsia-500 flex items-center justify-center">
                    <User className="w-16 h-16 text-white" />
                  </div>
                )}
              </div>
            </div>

            <div className="text-center">
              <p className="text-lg font-medium text-white">{user?.display_name}</p>
              <p className="text-sm text-slate-400">{user?.email}</p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 w-full">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="profile-picture-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex-1 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-white"
                data-testid="upload-profile-btn"
              >
                {uploading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Camera className="w-5 h-5 mr-2" />
                    {user?.profile_picture ? 'Change Photo' : 'Upload Photo'}
                  </>
                )}
              </Button>
              {user?.profile_picture && (
                <Button
                  onClick={handleDeletePicture}
                  disabled={uploading}
                  variant="ghost"
                  className="rounded-xl text-red-400 hover:text-red-300 hover:bg-red-950/30"
                  data-testid="delete-profile-btn"
                >
                  <Trash2 className="w-5 h-5" />
                </Button>
              )}
            </div>

            <p className="text-xs text-slate-500 text-center">
              Supported formats: JPEG, PNG, GIF, WebP (max 5MB)
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <SupportWidget open={supportOpen} onClose={() => setSupportOpen(false)} />
    </div>
  );
};

export default Layout;
